import React, { useState, useEffect, useMemo } from 'react';
import { Account, Campaign } from '../../types/client-management.types';
import { Company } from 'src/pages/billing/types/billing.types';
import { Briefcase, Building } from 'lucide-react';

interface ManageClientAccountCampaignSelectionProps {
  accounts: Account[];
  campaigns: Campaign[];
  companies?: Company[];
  selectedCompanies?: string[];
  selectedAccountIds: string[];
  selectedCampaignIds: string[];
  onSelectionChange: (accountIds: string[], campaignIds: string[]) => void;
  showCompanyGrouping?: boolean;
  disabled?: boolean;
  searchValue?: string;
  accountFilter?: string[];
  companyFilter?: string[];
}

type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

interface TreeNode {
  id: string;
  label: string;
  type: 'company' | 'account' | 'campaign';
  source?: string;
  children?: TreeNode[];
  accountId?: string; // For campaigns, track which account they belong to
  companyId?: string; // For accounts, track which company they belong to
  index?: number;
}

const ManageClientAccountCampaignSelection: React.FC<ManageClientAccountCampaignSelectionProps> = ({
  searchValue = '',
  accounts,
  campaigns,
  companies = [],
  selectedCompanies = [],
  selectedAccountIds,
  selectedCampaignIds,
  onSelectionChange,
  showCompanyGrouping = false,
  disabled = false,
  accountFilter = [],
  companyFilter = [],
}) => {

  const [localStartingAccountIds, setLocalStartingAccountIds] = useState<string[]>(selectedAccountIds);
  const [localStartingCampaignIds, setLocalStartingCampaignIds] = useState<string[]>(selectedCampaignIds);
  const [localSelectedAccountIds, setLocalSelectedAccountIds] = useState<string[]>(selectedAccountIds);
  const [localSelectedCampaignIds, setLocalSelectedCampaignIds] = useState<string[]>(selectedCampaignIds);
  const [localSelectedCompanies, setLocalSelectedCompanies] = useState<string[]>(selectedCompanies);

  // Sync with external props
  useEffect(() => {
    setLocalSelectedCompanies(selectedCompanies);
    setLocalStartingAccountIds(selectedAccountIds);
    setLocalStartingCampaignIds(selectedCampaignIds);
    setLocalSelectedAccountIds(selectedAccountIds);
    setLocalSelectedCampaignIds(selectedCampaignIds);
  }, [selectedAccountIds, selectedCampaignIds]);

  // Helper function to check if a label matches the search value
  const matchesSearch = (label: string): boolean => {
    if (!searchValue || searchValue.trim() === '') return true;
    return label.toLowerCase().includes(searchValue.toLowerCase().trim());
  };

  // Filter tree nodes based on search (only campaigns are filtered)
  const filterTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
    if (!searchValue || searchValue.trim() === '') {
      return nodes;
    }

    return nodes
      .map(node => {
        if (node.type === 'company') {
          // Filter children (accounts) - only include company if it has matching campaigns
          const filteredChildren = filterTreeNodes(node.children || []);

          // Include company only if any of its children (accounts with matching campaigns) match
          if (filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        }

        if (node.type === 'account') {
          // Filter children (campaigns) - only filter campaigns, not the account itself
          const filteredChildren = (node.children || []).filter(child =>
            matchesSearch(child.label)
          );

          // Include account only if any of its campaigns match
          if (filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        }

        // For campaigns, check if they match the search
        return matchesSearch(node.label) ? node : null;
      })
      .filter((node): node is TreeNode => node !== null);
  };

  // Build tree structure
  const treeData = useMemo(() => {
    let builtTree: TreeNode[];

    // Apply filters
    let filteredCompanies = companies;
    if (companyFilter.length > 0) {
      filteredCompanies = companies.filter(company => companyFilter.includes(company.id));
    }

    let filteredAccounts = accounts;
    if (accountFilter.length > 0) {
      filteredAccounts = accounts.filter(account => accountFilter.includes(account.id));
    }

    // Filter campaigns to only include those from filtered accounts
    const filteredAccountIds = new Set(filteredAccounts.map(acc => acc.id));
    const filteredCampaigns = campaigns.filter((campaign) => (
      Boolean(campaign?.account?.id) && filteredAccountIds.has(campaign.account.id)
    ));

    if (showCompanyGrouping && filteredCompanies.length > 0) {
      // Group by company -> account -> campaign
      const companyMap = new Map<string, TreeNode>();

      const buildAccountNodes = (companyAccounts: Account[], index: number): TreeNode[] => (
        companyAccounts.map((account) => {
          const accountCampaigns = filteredCampaigns.filter(
            (camp) => camp.account?.id === account.id
          );
          return {
            id: account.ext_account_id,
            label: account.title,
            type: 'account' as const,
            source: account.source,
            companyId: account.company ?? undefined,
            index,
            children: accountCampaigns.map((campaign) => ({
              id: campaign.ext_campaign_id,
              label: campaign.title,
              type: 'campaign' as const,
              source: account.source,
              accountId: account.ext_account_id,
              index,
            })),
          };
        }).filter((accountNode) => (accountNode.children?.length ?? 0) > 0)
      );

      filteredCompanies.forEach((company, index) => {
        const accountNodes = buildAccountNodes(
          filteredAccounts.filter((acc) => acc.company === company.id),
          index,
        );
        if (accountNodes.length === 0) return;

        companyMap.set(company.id, {
          id: company.id,
          label: company.company_name,
          type: 'company',
          children: accountNodes,
          index,
        });
      });

      // Accounts with no company (common for newly synced platforms like OpenAI Ads)
      // are otherwise invisible under company grouping.
      if (companyFilter.length === 0) {
        const unassignedAccounts = filteredAccounts.filter((acc) => !acc.company);
        const unassignedNodes = buildAccountNodes(unassignedAccounts, filteredCompanies.length);
        if (unassignedNodes.length > 0) {
          companyMap.set('__unassigned__', {
            id: '__unassigned__',
            label: 'Unassigned',
            type: 'company',
            children: unassignedNodes,
            index: filteredCompanies.length,
          });
        }
      }

      builtTree = Array.from(companyMap.values());
    } else {
      // Just account -> campaign (no company grouping)
      builtTree = filteredAccounts.map((account, index) => {
        const accountCampaigns = filteredCampaigns.filter(
          (camp) => camp.account?.id === account.id
        );
        return {
          id: account.ext_account_id,
          label: account.title,
          type: 'account',
          source: account.source,
          index: index,
          children: accountCampaigns.map(campaign => ({
            id: campaign.ext_campaign_id,
            label: campaign.title,
            type: 'campaign',
            source: account.source,
            accountId: account.ext_account_id,
            index: index,
          })),
        };
      }).filter((accountNode) => (accountNode.children?.length ?? 0) > 0);
    }

    // Apply search filter
    return filterTreeNodes(builtTree);
  }, [accounts, campaigns, companies, showCompanyGrouping, searchValue, accountFilter, companyFilter]);

  // Get checkbox state for a node
  const getCheckboxState = (node: TreeNode): CheckboxState => {
    if (node.type === 'campaign') {
      return localSelectedCampaignIds.includes(node.id) ? 'checked' : 'unchecked';
    }

    if (node.type === 'account') {
      const accountCampaigns = node.children || [];
      if (accountCampaigns.length === 0) {
        return localSelectedAccountIds.includes(node.id) ? 'checked' : 'unchecked';
      }

      const selectedCampaigns = accountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return 'unchecked';
      }
      if (selectedCampaigns.length === accountCampaigns.length) {
        return 'checked';
      }
      return 'indeterminate';
    }

    if (node.type === 'company') {
      const allAccountCampaigns = node.children?.flatMap(acc => acc.children || []) || [];
      if (allAccountCampaigns.length === 0) {
        const allAccountsSelected = node.children?.every(acc => localSelectedAccountIds.includes(acc.id)) || false;
        return allAccountsSelected ? 'checked' : 'unchecked';
      }

      const selectedCampaigns = allAccountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return 'unchecked';
      }
      if (selectedCampaigns.length === allAccountCampaigns.length) {
        return 'checked';
      }
      return 'indeterminate';
    }

    return 'unchecked';
  };

  const getNodeSelectionState = (node: TreeNode): boolean => {
    if (node.type === 'campaign') {
      return localSelectedCampaignIds.includes(node.id);
    }

    if (node.type === 'account') {
      const accountCampaigns = node.children || [];
      if (accountCampaigns.length === 0) {
        return localSelectedAccountIds.includes(node.id);
      }

      const selectedCampaigns = accountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return false;
      }
      if (selectedCampaigns.length === accountCampaigns.length) {
        return true;
      }
      return false;
    }

    if (node.type === 'company') {
      const allAccountCampaigns = node.children?.flatMap(acc => acc.children || []) || [];
      if (allAccountCampaigns.length === 0) {
        const allAccountsSelected = node.children?.every(acc => localSelectedAccountIds.includes(acc.id)) || false;
        return allAccountsSelected;
      }

      const selectedCampaigns = allAccountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return false;
      }
      if (selectedCampaigns.length === allAccountCampaigns.length) {
        return true;
      }
      return false;
    }

    return false;
  };

  // Handle node selection
  const handleNodeToggle = (node: TreeNode) => {
    if (disabled) return;

    if (node.type === 'campaign') {
      // Toggle campaign
      const newCampaignIds = localSelectedCampaignIds.includes(node.id)
        ? localSelectedCampaignIds.filter(id => id !== node.id)
        : [...localSelectedCampaignIds, node.id];

      setLocalSelectedCampaignIds(newCampaignIds);

      // If all campaigns for an account are selected, auto-select the account
      if (node.accountId) {
        // node.accountId is ext_account_id, find account by ext_account_id
        const account = accounts.find(a => a.ext_account_id === node.accountId);
        if (account) {
          const accountCampaigns = campaigns.filter((c) => c.account?.id === account.id);
          // Compare using ext_campaign_id since newCampaignIds contains ext_campaign_id values
          const allSelected = accountCampaigns.every(c => newCampaignIds.includes(c.ext_campaign_id));
          const newAccountIds = allSelected
            ? [...new Set([...localSelectedAccountIds, account.ext_account_id])]
            : localSelectedAccountIds.filter(id => id !== account.ext_account_id);

          setLocalSelectedAccountIds(newAccountIds);
          onSelectionChange(newAccountIds, newCampaignIds);
          return;
        }
      }

      onSelectionChange(localSelectedAccountIds, newCampaignIds);
    } else if (node.type === 'account') {
      // Toggle account - when selected, select all its campaigns by default
      const accountCampaigns = node.children || [];
      const accountCampaignIds = accountCampaigns.map(c => c.id);

      const isCurrentlySelected = localSelectedAccountIds.includes(node.id);

      if (isCurrentlySelected) {
        // Deselect account and all its campaigns
        const newAccountIds = localSelectedAccountIds.filter(id => id !== node.id);
        const newCampaignIds = localSelectedCampaignIds.filter(id => !accountCampaignIds.includes(id));
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      } else {
        // Select account and all its campaigns
        const newAccountIds = [...localSelectedAccountIds, node.id];
        const newCampaignIds = [...new Set([...localSelectedCampaignIds, ...accountCampaignIds])];
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      }
    } else if (node.type === 'company') {
      // Toggle company - select/deselect all accounts and campaigns under it
      const allAccounts = node.children || [];
      const allCampaigns = allAccounts.flatMap(acc => acc.children || []);
      const allAccountIds = allAccounts.map(acc => acc.id);
      const allCampaignIds = allCampaigns.map(c => c.id);

      const isCurrentlySelected = getCheckboxState(node) === 'checked';

      if (isCurrentlySelected) {
        // Deselect all
        const newAccountIds = localSelectedAccountIds.filter(id => !allAccountIds.includes(id));
        const newCampaignIds = localSelectedCampaignIds.filter(id => !allCampaignIds.includes(id));
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      } else {
        // Select all
        const newAccountIds = [...new Set([...localSelectedAccountIds, ...allAccountIds])];
        const newCampaignIds = [...new Set([...localSelectedCampaignIds, ...allCampaignIds])];
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      }
    }
  };

  const getNodeTextStyle = (node: TreeNode) => {
    if (node.type === 'company') {
      return 'font-bold';
    }
    if (node.type === 'account') {
      return 'font-semibold';
    }
    return 'font-normal';
  };

  const renderNodeIcon = (node: TreeNode) => {
    if (node.type === 'company') {
      return <Building className='w-4 h-4' />;
    }
    if (node.type === 'account') {
      return <Briefcase className='w-4 h-4' />;
    }
    return null;
  };

  const getNodeBgColor = (node: TreeNode) => {
    if (getNodeSelectionState(node)) {
      if (node.type === 'company') {
        return 'bg-green-501 text-white hover:bg-green-501/90';
      }
      if (node.type === 'account') {
        return 'bg-green-501/80 text-white hover:bg-green-501/70';
      }
      if (node.type === 'campaign') {
        return 'bg-green-501/60 text-white hover:bg-green-501/50';
      }
      return 'bg-green-501 text-white hover:bg-green-501/80';
    }

    if (node.type === 'company') {
      return 'bg-gray-201 hover:bg-gray-400';
    }
    if (node.type === 'account') {
      return 'bg-gray-200 hover:bg-gray-201/60';
    }
    return 'bg-gray-200/30 hover:bg-gray-201/50';
  };

  // Render tree node
  const renderTreeNode = (node: TreeNode, level: number = 0) => {
    const paddingLeft = level * 30;

    return (
      <div
        key={node.id}
        className="select-none"
      >
        <div
          className={`flex items-center gap-2 cursor-pointer px-2 py-2
            ${getNodeBgColor(node)}
            // ${getNodeSelectionState(node) ? 'bg-green-501 text-white' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''} `}
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
          onClick={() => !disabled && handleNodeToggle(node)}
        >
          {/* {renderCheckbox(node)} */}
          {renderNodeIcon(node)}
          <span className={`flex-1 text-sm ${getNodeTextStyle(node)}`}>
            {node.label}
            {node.source && (
              <span className='text-xs font-light ml-1'>
                (
                {node.source.replace('_', ' ').toLowerCase()}
                )
              </span>
            )}
          </span>
          {node.type === 'company' && node.children && node.children.length > 0 && (
            <span className="text-xs">
              (
              {node.children.length}
              )
            </span>
          )}
          {node.type === 'account' && node.children && node.children.length > 0 && (
            <span className="text-xs">
              (
              {node.children.length}
              )
            </span>
          )}
        </div>
        {node.children && node.children.length > 0 && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      <div className="border border-gray-300 rounded-md bg-white max-h-[600px] min-h-[400px] overflow-y-auto">
        {treeData.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No accounts or campaigns available
          </div>
        ) : (
          <div className="h-full">
            {treeData.map((node) => renderTreeNode(node as TreeNode))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageClientAccountCampaignSelection;
